import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { FavoriteType } from 'chaika-frontend-types/frontend-cms';

import { FavoriteApi } from '@api/FavoriteApi';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { store } from '@store';

import { DrugFavoriteItem } from '../utils/drugFavoritesSelectors';

export const drugFavoritesKeys = {
    all: ['drugFavorites'] as const,
    lists: () => [...drugFavoritesKeys.all, 'list'] as const,
    list: (userId: string) => [...drugFavoritesKeys.lists(), userId] as const,
};

export const drugFavoritesQueryOptions = {
    list: (userId: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: drugFavoritesKeys.list(userId),
            queryFn: () => fetchDrugFavorites(userId),
            enabled: userId.length > 0,
        }),
};

export function useDrugFavoritesQuery(): UseQueryResult<DrugFavoriteItem[]> {
    return useQuery(drugFavoritesQueryOptions.list(store.authUser.id));
}

async function fetchDrugFavorites(userId: string): Promise<DrugFavoriteItem[]> {
    const favorites = (await FavoriteApi.getList(userId, { types: [FavoriteType.Drug] })) as DrugFavoriteItem[];

    await loadDrugDictionaryRelations(favorites);

    // MobX-гейт: держим модель drugFavoriteList наполненной для внешних читателей (patient EntryDrug и т.п.),
    // пока источник загрузки — RQ. Снимается при полном отказе от MobX-модели.
    store.authUser.favorites.drugFavoriteList.set(favorites);

    return favorites;
}

async function loadDrugDictionaryRelations(favorites: DrugFavoriteItem[]): Promise<void> {
    const dictionaryItemIds = new Set<string>();

    favorites.forEach(({ data }) => {
        const drugs = Array.isArray(data) ? data : [data];

        drugs.forEach(({ activeSubstance, tradeNameIds = [] }) => {
            dictionaryItemIds.add(activeSubstance);
            tradeNameIds.forEach(id => dictionaryItemIds.add(id));
        });
    });

    if (dictionaryItemIds.size > 0) {
        await dictionaryService.loadDictionaryItemsByIds([...dictionaryItemIds]);
    }
}
